import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthPageShell } from '@/features/auth/components/auth-page-shell'
import { normalizeReturnUrl } from '@/features/auth/guards'
import { registerWithPassword } from '@/features/auth/services/auth-service'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { buildDeviceFingerprint } from '@/features/auth/utils/device-fingerprint'

type RegisterSearch = {
  redirect?: string
}

export const Route = createFileRoute('/register')({
  validateSearch: (search: Record<string, unknown>): RegisterSearch => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: RegisterRoute,
})

function RegisterRoute() {
  const navigate = useNavigate()
  const { redirect } = Route.useSearch()
  const setSession = useAuthSessionStore((state) => state.setSession)
  const setInitialized = useAuthSessionStore((state) => state.setInitialized)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const target = normalizeReturnUrl(redirect)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!name.trim() || !email.trim() || !password) {
      toast.error('请完整填写注册信息')
      return
    }

    if (password !== confirmPassword) {
      toast.error('两次输入的密码不一致')
      return
    }

    setIsSubmitting(true)

    try {
      const session = await registerWithPassword({
        name: name.trim(),
        email: email.trim(),
        password,
        deviceFingerprint: await buildDeviceFingerprint(),
      })
      setSession(session)
      setInitialized(true)
      toast.success('验证码已发送，请先验证邮箱')
      await navigate({ to: '/verify-email', search: { redirect: target } })
    } catch (error) {
      toast.error(error instanceof Error ? error.message : '注册失败，请稍后重试')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthPageShell
      title="注册 JIAN 账号"
      description="注册后即可使用 AI 配音、生图、视频生成等全部工具。"
      footerText="已经有账号？"
      footerActionLabel="去登录"
      footerActionTo="/login"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div className="space-y-2">
          <Label htmlFor="name">昵称</Label>
          <Input
            id="name"
            autoComplete="nickname"
            placeholder="请输入昵称"
            className="auth-input"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">邮箱</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="auth-input"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">密码</Label>
          <div className="relative"><Input
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            placeholder="至少 8 位密码"
            className="auth-input"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          /><button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-2.5 text-[#697184]" aria-label={showPassword ? '隐藏密码' : '显示密码'}>{showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}</button></div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm-password">确认密码</Label>
          <Input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            placeholder="再次输入密码"
            className="auth-input"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
          />
        </div>
        <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
          {isSubmitting ? '注册中...' : '注册并进入工作台'}
        </Button>
      </form>
    </AuthPageShell>
  )
}
